import { FC, useContext, useEffect, useMemo, useState } from 'react';

import { useQuery } from '@apollo/client';
import {
  Button,
  ContextMenu,
  IContextMenuItem,
  useContextMenu,
  EditIcon,
  BucketIcon,
  ShutdownIcon,
  Table,
} from '@smartshell/ui-kit';
import dayjs from 'dayjs';

import { LayoutContext } from 'components/Layout';
import { LocalizationContext } from 'localization';

import s from './Cashback.module.scss';
import { CashbackManager } from './CashbackManager';
import { GET_CASHBACKS, IGetCashbacksResponse } from './schema';
import { TCashbackRule } from './types';
import { useCashbackRuleMutations } from './useCashbackRuleMutations';
import { UseGetColumns } from './UseGetColumns';

export const Cashback: FC = () => {
  const { t } = useContext(LocalizationContext);
  const layoutElement = useContext(LayoutContext);

  const { deleteCashback, updateCashback, isDeleting, isUpdating } =
    useCashbackRuleMutations();
  const isProcessing = isDeleting || isUpdating;
  const { data: loadedCashbacks, loading } = useQuery<IGetCashbacksResponse>(
    GET_CASHBACKS,
    {
      fetchPolicy: 'cache-and-network',
    }
  );

  const [selectedCashbackId, setSelectedCashbackId] = useState<number | null>(
    null
  );
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (!loadedCashbacks?.depositCashbacks) return;

    if (
      !loadedCashbacks.depositCashbacks.some(
        (cashback) => cashback.id === selectedCashbackId
      )
    ) {
      setSelectedCashbackId(null);
    }
  }, [loadedCashbacks]);

  const cashbacks = loadedCashbacks?.depositCashbacks;

  const {
    contextMenuData,
    closeContextMenu,
    cursorPosition,
    onContextMenu,
    onTouchMove,
    onTouchEnd,
    onTouchStart,
  } = useContextMenu<TCashbackRule>();
  const contextMenuItems: Array<IContextMenuItem<TCashbackRule>> = useMemo(
    () => [
      {
        key: 'edit',
        Icon: EditIcon,
        text: t('edit'),
        onClick: (rule) => {
          setIsOpen(true);
          setSelectedCashbackId(rule.id);
        },
      },
      {
        key: 'disable',
        Icon: ShutdownIcon,
        text: (rule) => t(rule.is_active ? 'cashback.off' : 'cashback.on'),
        disabled: (rule) =>
          (rule.expires_at && dayjs(rule.expires_at).isBefore(dayjs())) ||
          isProcessing,
        onClick: ({ id: ruleId, ...restTRule }) =>
          updateCashback({
            variables: {
              id: ruleId,
              input: { ...restTRule, is_active: !restTRule.is_active },
            },
          }),
      },
      {
        key: 'delete',
        Icon: BucketIcon,
        text: t('delete'),
        disabled: isProcessing,
        onClick: (rule: TCashbackRule) =>
          deleteCashback({ variables: { id: rule.id } }),
        className: s.remove,
      },
    ],
    [isProcessing]
  );

  const COLUMNS = UseGetColumns();

  return (
    <div className={s.container}>
      {contextMenuData && cursorPosition && (
        <ContextMenu
          layoutElement={layoutElement}
          data={contextMenuData}
          items={contextMenuItems}
          closeContextMenu={closeContextMenu}
          position={cursorPosition}
        />
      )}
      {isOpen && (
        <CashbackManager
          cashbackId={selectedCashbackId}
          onSuccess={() => {
            setIsOpen(false);
            setSelectedCashbackId(null);
          }}
          onClose={() => {
            setIsOpen(false);
            setSelectedCashbackId(null);
          }}
        />
      )}
      <div className={s.list}>
        <Button
          className={s.add}
          onClick={() => {
            setSelectedCashbackId(null);
            setIsOpen(true);
          }}
          iconName="CirclePlusIcon"
        >
          {t('cashbacks.add')}
        </Button>
        <Table
          data={cashbacks ?? []}
          columns={COLUMNS}
          isLoading={loading}
          emptyDataPlaceholder={t('cashbacks.placeholder')}
          onContextMenu={(event, data) => {
            onContextMenu(event, data);
          }}
          onTouchStart={(event, row) => {
            onTouchStart(event, row);
          }}
          onRowDoubleClick={(_, cashback) => {
            setSelectedCashbackId(cashback?.id);
            setIsOpen(true);
          }}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          className={s.table}
        />
      </div>
    </div>
  );
};
